/* @flow */
// //////////////////////////////////////////////////////////////////////////////////////////////
// Ad System JS Api                                                                           //
//                                                                                            //
// $Id: as-4.1.0.min.js.map,v 1.4 2026/08/13 07:25:12 indran Exp $                                     //
// $Source: /proj/cvs/untd_adtech/server/vas_web/js_api/dist/as-4.1.0.min.js.map,v $                         //
// //////////////////////////////////////////////////////////////////////////////////////////////

/** import required es6 polyfill from babel-polyfill */
/**
 * To speed up flow, we are not going through core-js in node modules. Suppress below
 * import warnings
 * This **SHOULD** be always at the top
 */

/**
 * @description Implements AdServing API
 * @module JSAPI
 * @exports __as
 * @requires {@link module:adconfig}
 * @requires {@link module:adconfigUpdater}
 * @requires {@link module:adrotation}
 * @requires {@link module:adloader}
 * @requires {@link module:adserver}
 * @requires {@link module:globalParams}
 */

import adconfig from './lib/adconfig';
import adconfigUpdater from './lib/adconfig-updater';
import adrotation from './lib/adrotation';
import adloader from './lib/adloader';
import adserver from './lib/adserver';
import globalParams from './lib/globalparams';
import { initOrUpdateGPPAPI } from './lib/utils/gpputils';
import { noop } from './lib/utils/coreutils';

class ASObj {
  _w: WindowProxy;

  _d: Document;

  _bInited: boolean;

  /**
   * @class
   * @param {window} w Window Object
   * @param {Document} d Document
   */
  constructor(w: WindowProxy, d: Document) {
    this._w = w;
    this._d = d;
    this._bInited = false;
  }

  /**
   * initialize API
   * If global params is not passed, '__asInfo' global config is used(if defined)
   *
   * @param {module:globalParams.globalParam} [gParams] Globalparams object to initialize with
   * @returns {undefined}
   */
  init(gParams?: globalParam = this._w.__asInfo) {
    if (this._bInited) {
      return;
    }

    this._bInited = true;
    if (!globalParams.ValidateAndUpdateConfig(gParams)) {
      return; // invalid config
    }

    // get updated config
    const g: globalParam = globalParams.get();

    // initialize config, loader, adserver, rotation objects
    adconfigUpdater.init(this._w, this._d, g);
    // $FlowFixMe[method-unbinding]
    // $FlowFixMe[class-object-subtyping]
    adrotation.setGetAdsCB(this.get, this);
  }

  /**
   * load ads
   *
   * @private
   * @param {string} pageName - Page name
   * @param {Array<string>} posListArr - List of ad positions
   * @param {boolean} bRotation - For ad rotation?
   * @returns {undefined}
   */
  _loadAds(pageName: string, posListArr: Array<string>, bRotation: boolean) {
    // Early return if ads disabled for EU users
    if (adconfig.isAdsDisabledForEU()) {
      return;
    }
    adserver.clearAdRequests(pageName, posListArr, bRotation);
    adserver.loadAds(pageName, posListArr, bRotation);
  }

  /**
   * get Ads for current page
   * All the parameters are optional. In missing cases, callbacks are used to get required info
   *
   * @param {string} [page] - Page name
   * @param {Array<string>} [posList] - List of ad positions as Array|comma-separated list
   * @param {boolean} [bCache=true] - Should cache this list?
   * @param {boolean} [bRotate=false] - For rotation?
   * @returns {undefined}
   */
  get(
    page: ?string,
    posList: ?(string | Array<string>),
    bCache: boolean,
    bRotate: boolean
  ) {
    // let us intialize, init takes care of returning if already initialized
    this.init();

    // If bCache is true, we store the arguments passed. By default its true.
    // In eow to toggle RightAd, we should not reset posList used for rotation.
    // In this case eow needs to pass bCache as false to avoid caching.
    if (bCache !== false) {
      adconfig.setPageInfo(page, posList);
    }

    // clear adRotation and start timer
    adrotation.clear();
    if (bRotate !== true) {
      adrotation.setPageStart();
    }
    adrotation.setLastRotationTime();
    adloader.clearList();

    // page, posList can be configured as callback functions in global params
    // page, posList passed as arguments to _getAds are given more precedence
    const pageName = adconfig.getPageName(page);

    // load config before loading ads - getPagePositions must be called AFTER config loads
    // to ensure excludePositions from config are applied
    const loadAdsAfterConfig = () => {
      const posListArr = adconfig.getPagePositions(posList, pageName);
      return this._loadAds(pageName, posListArr, bRotate === true);
    };
    adconfigUpdater.load().then(loadAdsAfterConfig, loadAdsAfterConfig);
  }

  /**
   * Force update the Ad config
   * @param {boolean} bForce - For update or not
   * @returns {undefined}
   */
  updateConfig(bForce: boolean = false) {
    adconfigUpdater.load(bForce).catch(noop);
  }

  /**
   * Refresh privacy signals (GPP/CCPA) and re-apply ad-server privacy state after
   * usprivacy cookie changes — e.g. cookie banner Accept/Reject (COM-7697).
   * Set usprivacy before calling. Optional __as.get() for new ad slots.
   *
   * @returns {undefined}
   */
  refreshPrivacySignals(): void {
    initOrUpdateGPPAPI(true);
    adserver.refreshPrivacySignals();
  }

  /**
   * populate custom variables inside iframe for old IE
   *
   * @param {window} win - window object
   * @param {Document} doc - document object
   * @param {string} id - Position ID
   * @returns {undefined}
   */
  populateCustomVar(win: WindowProxy, doc: Document, id: string): void {
    return adloader.populateCustomVar(win, doc, id);
  }

  /**
   * render header bidding partner ads
   *
   * @param {window} win - window object
   * @param {Document} doc - document object
   * @param {string} pos - ad position name
   * @param {string} partner - partner name
   * @param {string} param  - Extra param
   * @returns {undefined}
   */
  renderAd(
    win: WindowProxy,
    doc: Document,
    pos: string,
    partner: string,
    param: string
  ) {
    if (!win.UOL_POS) {
      this.populateCustomVar(win, doc, pos);
    }
    adserver.renderBidAds(win, doc, pos, partner, param);
  }

  /**
   * utility function for ad page to get data
   *
   * @param {window} win - window object
   * @returns {module:adloader.adInfo} adInfo object
   */
  getAdPageInfo(win: WindowProxy /* , doc */): adInfo {
    return adloader.getIFrameAdInfo(win);
  }

  /**
   * toggle ad for ad rotation
   *
   * @param {string} adPos - Ad position name
   * @returns {undefined}
   */
  toggleAd(adPos: string) {
    adloader.toggleAD(adPos);
  }
}

// expose our object in window scope & our module object
// export default new asObj(window, document);
const __as: ASObj = new ASObj(window, document);
// GPP CMP API on script load so window.__gpp is ready before config/get() (Ezoic, etc.)
initOrUpdateGPPAPI();
// for webpack?
// module.exports = __as;
// Browserify needs this to populate as singleton object outside
// global.__as = __as;
// for rollup
export default __as;
